import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import {
  Button,
  Card,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { Table, Empty, Tag, Dropdown, Menu, Tabs } from "antd";
import { ReloadOutlined, PlusOutlined } from "@ant-design/icons";
import { ColumnType } from "antd/es/table";
import Swal from "sweetalert2";
import dayjs from "dayjs";

import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { Icon } from "../../components/Component";
import { updateMenuData } from "../../store/slices/misc/menuData";
import { updateReportInDetailModalSlice } from "../../store/slices/expenses/ReportInDetailView";
import TopMenu from "../../route/topMenu";
import { expensesApis } from "../../api/Expenses";
import SkeletonTable, { SkeletonTableColumnsType } from "../../utils/skeleton-templates/table";
import ReportInDetailModal from "./ReportInDetailModal";

interface ExpenseReport {
  id: string;
  reportName: string;
  status: string;
  submittedDate: string;
  approvedDate?: string;
  totalAmount: number;
  expenses: number;
  total: string;
  amountToBeReimbursed: string;
  submitter?: string;
  dateRange?: string;
  paginationId?: string;
  createdBy?: number;
  createdByName?: string;
  budgetLineId?: number;
  businessPurpose?: string;
  budgetValue?: number;
  approvalData?: ApprovalData[];
  // User-specific fields for the new column structure
  userTotalIn?: number;
  userTotalOut?: number;
  userBalance?: number;
  userReimbursableAmount?: number;
  userReimbursableBalance?: number;
  userNonReimbursableAmount?: number;
  userReimbursableTaxAmount?: number;
  userTotalAdvances?: number;
  // Original fields for backward compatibility - updated interface
  totalIn?: number;
  totalOut?: number;
  balance?: number;
  reimbursableAmount?: number;
  nonReimbursableAmount?: number;
  taxAmount?: number;
  extra?: {
    approvalData?: ApprovalData[];
    [key: string]: any;
  };
}
interface ApprovalData {
  roleId: number | null;
  level: number;
  userId: number | null;
  approvalStage?: string;
  roleName?: string;
  userName?: string;
}


const Reports: React.FC = () => {
  const methods = useForm({
    mode: "onChange", // This will trigger validation on every change
  });
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const RootPageRenderState = useSelector((state: any) => state.RootPageRenderState?.data);
  const params = useParams<{ projectId: string }>();
  const projectIdFromUrl = params.projectId;

  // Helper function to get userId consistently
  const getUserId = () => {
    const userId = currentUser?.id || currentUser?.userId || currentUser?.data?.id || currentUser?.data?.userId;
    return userId;
  };
  const { projectName, siteId } = location.state || {};
  const [projectId, setProjectId] = useState<string | number | null>(null);
  const [activeTab, setActiveTab] = useState("all");
  const [selectedReports, setSelectedReports] = useState<string[]>([]);
  const [sm, setSm] = useState(false);
  const [mobileView, setMobileView] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState<ExpenseReport[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [screenSize, setScreenSize] = useState(0);
  const isLoadingReports = useRef(false); // Prevent multiple simultaneous API calls

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    defaultCurrent: 1,
    defaultPageSize: 15,
    showSizeChanger: true,
    pageSizeOptions: ["15", "20", "30", "40", "50"],
    total: 0,
  });
  const [endOfList, setEndOfList] = useState(false);

  // Mobile Responsive: Mobile view detection and screen size tracking
  useEffect(() => {
    const checkMobileView = () => {
      setMobileView(window.innerWidth < 768);
      setScreenSize(window.innerWidth);
    };
    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    return () => window.removeEventListener('resize', checkMobileView);
  }, []);

  useEffect(() => {
    sm
      ? document.body.classList.add("toggle-shown")
      : document.body.classList.remove("toggle-shown");
  }, [sm]);

  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => {
          return {
            ...item,
            linkState: location.state,
          };
        })
      )
    );
  }, [location, dispatch]);

  // Navigation: Extract projectId from URL params first, then location state
  useEffect(() => {
    const { id: projectIdFromState, projectId: projectIdAlt, openReportBreakDown, refreshReportBreakDown, reportId, reportName } = location.state || {};
    // Use URL param first, then fall back to location state
    const resolvedProjectId = projectIdFromUrl || projectIdFromState || projectIdAlt;
    if (resolvedProjectId && !isNaN(Number(resolvedProjectId))) {
      setProjectId(resolvedProjectId);
    }

    // Handle opening ReportBreakDown modal with refresh flag
    if (openReportBreakDown && reportId && resolvedProjectId) {
      // Find the report from the current reports list
      const report = reports.find(r => r.id === reportId);

      if (report) {
        const modalData = {
          id: reportId.toString(),
          open: true,
          reportName: reportName || report.reportName || `Report ${reportId}`,
          activeTab: "expenses",
          projectResponse: {
            id: parseInt(resolvedProjectId.toString()),
            name: "Project"
          },
          extra: {
            status: report.status || "DRAFT",
            submittedDate: report.submittedDate || dayjs().format('DD-MMM-YYYY'),
            totalAmount: report.totalAmount || 0,
            expenses: report.expenses || 0,
            total: report.total || "₹0",
            amountToBeReimbursed: report.amountToBeReimbursed || "₹0",
            submitter: report.submitter,
            dateRange: report.dateRange || dayjs().format('DD-MMM-YYYY'),
            createdBy: report.createdBy || report.extra?.createdBy,
            createdByName: report.createdByName || report.extra?.createdByName || report.submitter,
            userTotalIn: report.userTotalIn || 0,
            userTotalOut: report.userTotalOut || 0,
            userBalance: report.userBalance || 0,
            userReimbursableAmount: report.userReimbursableAmount || 0,
            totalIn: report.totalIn || 0,
            totalOut: report.totalOut || 0,
            balance: report.balance || 0,
            reimbursableAmount: report.reimbursableAmount || 0,
            nonReimbursableAmount: report.nonReimbursableAmount || 0,
            taxAmount: report.taxAmount || 0,
            expenseApprovalHierarchy: report.extra?.approvalData || []
          },
          allowEdit: true,
          refreshReportBreakDown: refreshReportBreakDown || true,
          sourcePage: 'reports'
        };
        dispatch(updateReportInDetailModalSlice(modalData));
      } else {
        const modalData = {
          id: reportId.toString(),
          open: true,
          reportName: reportName || `Report ${reportId}`,
          activeTab: "expenses",
          projectResponse: {
            id: parseInt(resolvedProjectId.toString()),
            name: "Project"
          },
          extra: {
            submittedDate: dayjs().format('DD-MMM-YYYY'),
            totalAmount: 0,
            expenses: 0,
            total: "₹0",
            amountToBeReimbursed: "₹0",
            submitter: null,
            dateRange: dayjs().format('DD-MMM-YYYY'),
            createdBy: null,
            createdByName: null,
            userTotalIn: 0,
            userTotalOut: 0,
            userBalance: 0,
            userReimbursableAmount: 0,
            totalIn: 0,
            totalOut: 0,
            balance: 0,
            reimbursableAmount: 0,
            nonReimbursableAmount: 0,
            taxAmount: 0
          },
          allowEdit: true,
          refreshReportBreakDown: refreshReportBreakDown || true,
          sourcePage: 'reports'
        };
        dispatch(updateReportInDetailModalSlice(modalData));
      }

      navigate(location.pathname, {
        replace: true,
        state: {
          ...location.state,
          openReportBreakDown: false,
          refreshReportBreakDown: false
        }
      });
    }
  }, [location.state, dispatch, navigate, location.pathname, projectIdFromUrl]);

  // Data Loading: Consolidated data loading effect - prevents multiple API calls
  useEffect(() => {
    const shouldLoadData = () => {
      const resolvedProjectId = projectId || projectIdFromUrl || location.state?.projectId;
      return resolvedProjectId && !isNaN(Number(resolvedProjectId)) && getUserId();
    };

    if (shouldLoadData()) {
      refreshListView();
    }
  }, [projectId, currentUser, RootPageRenderState, projectIdFromUrl]);

  const loadReports = async (params?: any, append = false) => {
    if (isLoadingReports.current) {
      return;
    }

    isLoadingReports.current = true;
    setLoading(true);
    try {
      const resolvedProjectId = projectId || projectIdFromUrl || location.state?.projectId;
      if (resolvedProjectId && !isNaN(Number(resolvedProjectId))) {
        const formattedProjectId = Number(resolvedProjectId);
        const { sortOrder, pageSize, direction, pageDiff, paginationId } = params || {};

        const userId = getUserId();
        const isAdmin = currentUser?.genericRole?.toLowerCase() === "admin";
        if (!userId && !isAdmin) {
          setTimeout(() => {
            loadReports(params, append);
          }, 100);
          return;
        }

        const apiParams = {
          projectId: formattedProjectId,
          pageSize: pageSize || pagination.pageSize,
          pageDiff: pageDiff || 0,
          sortOrder: sortOrder || 'DESC',
          direction: direction || 'P',
          paginationId: paginationId || null,
          ...(!isAdmin && { userId: userId }),
          searchQuery: searchQuery || null,
        };

        const response = await expensesApis.fetchReports(formattedProjectId, apiParams);

        if (response && response.status) {
          const reportsList = response.data?.list || [];
          const totalCount = response.data?.count || 0;


          const transformedReports: ExpenseReport[] = reportsList.map((report: any) => {
            return {
              id: report.expenseReportId?.toString() || Date.now().toString(),
              reportName: report?.reportName,
              status: report.userReportStatus || report.reportStatus,
              submittedDate: report.createdAt ? dayjs(report.createdAt).format('DD-MMM-YYYY') : dayjs().format('DD-MMM-YYYY'),
              approvedDate: report.updatedAt ? dayjs(report.updatedAt).format('DD-MMM-YYYY') : undefined,
              totalAmount: report.totalOut || 0,
              expenses: report.expenses?.length || 0,
              total: `₹${(report.totalOut || 0).toLocaleString()}`,
              amountToBeReimbursed: `₹${(report.reimbursableAmount || 0).toLocaleString()}`,
              submitter: report.createdBy?.toString(),
              dateRange: report.startDate && report.endDate ? `${dayjs(report.startDate).startOf('day').format('DD-MMM-YYYY')} - ${dayjs(report.endDate).startOf('day').format('DD-MMM-YYYY')}` : '',
              paginationId: report.paginationId || report.expenseReportId,
              createdBy: report.createdBy,
              createdByName: report.createdByName,
              budgetLineId: report.budgetLineId || report.budgetLine?.lineId,
              businessPurpose: report.businessPurpose || '',
              budgetValue: report.budgetValue ? parseFloat(report.budgetValue) : undefined,
              approvalData: report.expenseApprovalHierarchy || report.approvalData || [],
              userTotalIn: report.userTotalIn || 0,
              userTotalOut: report.userTotalOut || 0,
              userBalance: report.userBalance || 0,
              userReimbursableAmount: report.userReimbursableAmount || 0,
              userNonReimbursableAmount: report.userNonReimbursableAmount || 0,
              userReimbursableTaxAmount: report.userReimbursableTaxAmount || 0,
              userTotalAdvances: report.userTotalAdvances || 0,
              totalIn: report.totalIn || 0,
              totalOut: report.totalOut || 0,
              balance: report.balance || (report.totalIn || 0) - (report.totalOut || 0),
              reimbursableAmount: report.reimbursableAmount || 0,
              nonReimbursableAmount: report.nonReimbursableAmount || 0,
              taxAmount: report.taxAmount || 0,
              extra: {
                approvalData: report.expenseApprovalHierarchy || report.approvalData || []
              }
            };
          });

          setPagination((prev) => ({
            ...prev,
            total: Number(totalCount),
          }));

          if (reportsList.length < (pageSize || pagination.pageSize)) {
            setEndOfList(true);
          } else {
            setEndOfList(false);
          }

          setReports((prev: any) => (append ? [...prev, ...transformedReports] : transformedReports));
        } else {
          setReports([]);
        }
      } else {
        setReports([]);
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      setReports([]);
    } finally {
      setLoading(false);
      isLoadingReports.current = false;
    }
  };

  const tableOnChange = async (
    newPagination: any,
    filters: any,
    sorter: any
  ) => {
    const apiParams: any = {
      oldPage: pagination.current,
      current: newPagination.current?.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize,
      direction: "P", // Default direction
      pageDiff: 0,
      paginationId: null,
    };

    if (sorter.order) {
      apiParams.sortOrder = sorter.order === "ascend" ? "ASC" : "DESC";
    } else if (newPagination.pageSize !== pagination.pageSize) {
      apiParams.pageSize = newPagination.pageSize;
      const _currentPage = Math.floor(
        ((Number(pagination.current) - 1) * Number(pagination.pageSize)) /
        Number(newPagination.pageSize)
      ) + 1;
      apiParams.current = _currentPage;
      apiParams.pageDiff = _currentPage - 1;
    } else {
      if (newPagination.current !== pagination.current) {
        const direction = newPagination.current > pagination.current ? "F" : "B";
        apiParams.direction = direction;

        if (direction === "F" && reports.length > 0) {
          apiParams.paginationId = reports[reports.length - 1]?.paginationId || null;
        } else if (direction === "B" && reports.length > 0) {
          apiParams.paginationId = reports[0]?.paginationId || null;
        }

        apiParams.pageDiff = Math.abs(newPagination.current - pagination.current) - 1;
      }
    }

    setPagination({
      ...pagination,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });

    await loadReports({
      ...apiParams,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  // Data Loading: Refresh list view function
  const refreshListView = () => {
    try {
      setEndOfList(false);
      setPagination({
        current: 1,
        pageSize: 15,
        defaultCurrent: 1,
        defaultPageSize: 15,
        showSizeChanger: true,
        pageSizeOptions: ["15", "20", "30", "40", "50"],
        total: 0,
      });
      loadReports({
        current: 1,
        pageSize: 15,
        pageDiff: 0,
        paginationId: null,
        direction: "P",
        sortOrder: "DESC",
        searchQuery: searchQuery
      });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
    }
  };


  // Report Detail Modal: Open report breakdown modal with existing data
  const handleOpenReport = async (report: ExpenseReport) => {
    const modalData = {
      id: report.id,
      open: true,
      reportName: report.reportName,
      activeTab: "expenses",
      projectResponse: {
        id: Number(projectId || location.state?.projectId),
        name: projectName || "Project",
        projectId: Number(projectId || location.state?.projectId)
      },
      extra: {
        submittedDate: report.submittedDate,
        approvedDate: report.approvedDate,
        totalAmount: report.totalAmount,
        expenses: report.expenses,
        total: report.total,
        amountToBeReimbursed: report.amountToBeReimbursed,
        submitter: report.submitter,
        dateRange: report.dateRange,
        paginationId: report.paginationId,
        createdBy: report.createdBy || report.submitter,
        createdByName: report.createdByName || report.submitter,
        userTotalIn: report.userTotalIn || 0,
        userTotalOut: report.userTotalOut || 0,
        userBalance: report.userBalance || 0,
        userReimbursableAmount: report.userReimbursableAmount || 0,
        userReimbursableBalance: report.userReimbursableBalance || 0,
        userNonReimbursableAmount: report.userNonReimbursableAmount || 0,
        userReimbursableTaxAmount: report.userReimbursableTaxAmount || 0,
        userTotalAdvances: report.userTotalAdvances || 0,
        totalIn: report.totalIn || 0,
        totalOut: report.totalOut || 0,
        balance: report.balance || 0,
        reimbursableAmount: report.reimbursableAmount || 0,
        nonReimbursableAmount: report.nonReimbursableAmount || 0,
        taxAmount: report.taxAmount || 0,
        expenseApprovalHierarchy: report.extra?.approvalData || []
      },
      allowEdit: true,
      refreshReportBreakDown: true,
      sourcePage: 'reports'
    };
    dispatch(updateReportInDetailModalSlice(modalData));
  };

  // Report Detail Modal: Handle ID click to show report breakdown
  const handleIdClick = async (reportId: string) => {
    if (!reportId) return;

    const report = reports.find(r => r.id === reportId);
    if (report) {
      handleOpenReport(report);
    } else {
      Swal.fire({
        title: "Error!",
        text: "Report not found",
        icon: "error",
        confirmButtonText: "OK"
      });
    }
  };

  // Report Actions: Navigate to edit report page
  const handleEditReport = (report: ExpenseReport) => {
    const resolvedProjectId = projectId || projectIdFromUrl || location.state?.projectId;
    navigate(resolvedProjectId ? `/pms/expenses/new-header/${resolvedProjectId}` : '/pms/expenses/new-header', {
      state: {
        ...location.state,
        id: resolvedProjectId,
        projectId: resolvedProjectId,
        projectName: projectName || location.state?.projectName,
        siteId: siteId || location.state?.siteId,
        editMode: true,
        reportData: report
      }
    });
  };

  // Report Actions: Delete report with confirmation
  const handleDeleteReport = async (reportId: string) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#2557d6",
        confirmButtonText: "Yes, delete it!"
      });

      if (result.isConfirmed) {
        setLoading(true);
        try {
          const response = await expensesApis.deleteReport(reportId);

          if (response && response.status) {
            setReports(prev => prev.filter(report => report.id !== reportId));
            Swal.fire("Deleted!", "Report has been deleted.", "success");
          } else {
            throw new Error(response?.message || "Failed to delete report");
          }
        } catch (error: any) {
          const errorMessage = error?.response?.data?.message;
          Swal.fire({
            title: "Error!",
            text: errorMessage,
            icon: "error",
            confirmButtonText: "OK"
          });
        } finally {
          setLoading(false);
        }
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
    }
  };

  // Approval Workflow: Send individual report for approval
  const handleSendReportForApproval = async (reportId: string) => {
    try {
      setLoading(true);

      const response = await expensesApis.createReportApprovals({
        reportId: parseInt(reportId)
      });

      // Handle backend error response
      if (response?.response?.data) {
        Swal.fire({
          title: "Error!",
          text: response.response.data.message,
          icon: "error",
          confirmButtonText: "OK"
        });
        return;
      }

      // Handle success response
      if (response?.status === true) {
        setReports(prev =>
          prev.map(report =>
            report.id === reportId
              ? { ...report, status: "PENDING" }
              : report
          )
        );

        Swal.fire({
          title: "Success!",
          text: response.message || "Report sent for approval successfully",
          icon: "success",
          confirmButtonText: "OK"
        });
      } else {
        Swal.fire({
          title: "Error!",
          text: response?.message,
          icon: "error",
          confirmButtonText: "OK"
        });
      }
    } catch (error: any) {
      Swal.fire({
        title: "Error!",
        text: error?.response?.data?.message,
        icon: "error",
        confirmButtonText: "OK"
      });
    } finally {
      setLoading(false);
    }
  };

  // Bulk Operations: Send multiple reports for approval
  const handleBulkSendForApproval = async () => {
    if (selectedReports.length === 0) {
      Swal.fire({
        title: "No Selection",
        text: "Please select reports to send for approval",
        icon: "warning",
        confirmButtonText: "OK"
      });
      return;
    }

    Swal.fire({
      title: "Are you sure you want to send for approval?",
      text: `You are about to send ${selectedReports.length} report(s) for approval. You won't be able to revert this!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, send them!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          const results = await Promise.allSettled(
            selectedReports.map(reportId =>
              expensesApis.createReportApprovals({
                reportId: parseInt(reportId)
              })
            )
          );

          const successful = results.filter(result => {
            if (result.status === 'fulfilled') {
              const response = result.value;
              if (response && response.response && response.response.data) {
                return false;
              }
              return response && response.status === true;
            }
            return false;
          }).length;

          const failed = results.length - successful;

          if (successful > 0) {
            setReports(prev => prev.map(report =>
              selectedReports.includes(report.id)
                ? { ...report, status: 'PENDING' }
                : report
            ));

            setSelectedReports([]);

            if (failed > 0) {
              Swal.fire({
                title: "Partial Success!",
                text: `${successful} report(s) sent for approval successfully, ${failed} failed`,
                icon: "warning",
                confirmButtonText: "OK"
              });
            } else {
              Swal.fire({
                icon: "success",
                title: "Success",
                text: `${successful} report(s) sent for approval successfully`,
                confirmButtonText: "Ok",
              });
            }
          } else {
            Swal.fire({
              title: "Error!",
              text: "Failed to send reports for approval. Please try again.",
              icon: "error",
              confirmButtonText: "OK"
            });
          }
        } catch (error: any) {
          const errorMessage = error?.response?.data?.message;
          Swal.fire({
            title: "Error!",
            text: errorMessage,
            icon: "error",
            confirmButtonText: "OK"
          });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const filteredReports = reports.filter(report => {
    const normalizedStatus = report.status?.toUpperCase() || '';
    if (activeTab === "pending" || activeTab === "pending-dropdown") {
      return normalizedStatus === "PENDING" || normalizedStatus === "PENDING_APPROVAL";
    }
    if (activeTab === "approved") {
      return normalizedStatus === "APPROVED";
    }
    if (activeTab === "rejected") {
      return normalizedStatus === "REJECTED";
    }
    if (activeTab === "draft") {
      return normalizedStatus === "DRAFT";
    }
    return true;
  });

  // Bulk Operations: Top right JSX for bulk actions - only show Send for Approval for draft and rejected reports
  const topRightJSX = selectedReports.length > 0 && filteredReports.some(report => selectedReports.includes(report.id) && (report.status?.toUpperCase() === 'DRAFT' || report.status?.toUpperCase() === 'REJECTED')) ? (
    <>
      {selectedReports.some(reportId => {
        const report = reports.find(r => r.id === reportId);
        return report && (report.status?.toUpperCase() === 'DRAFT' || report.status?.toUpperCase() === 'REJECTED');
      }) && (
          <li>
            <Button
              style={{
                backgroundColor: "#e6f7ff",
                color: "#1890ff",
                borderColor: "#e6f7ff",
                marginRight: "8px",
                borderRadius: "4px",
                height: "32px",
                fontSize: "12px",
                padding: "4px 12px",
                fontWeight: "500"
              }}
              onClick={handleBulkSendForApproval}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#1890ff";
                e.currentTarget.style.color = "#fff";
                e.currentTarget.style.borderColor = "#1890ff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#e6f7ff";
                e.currentTarget.style.color = "#1890ff";
                e.currentTarget.style.borderColor = "#e6f7ff";
              }}
            >
              Send for Approval
            </Button>
          </li>
        )}
    </>
  ) : null;

  // Bulk Operations: Row selection configuration
  const rowSelection = {
    selectedRowKeys: selectedReports,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedReports(newSelectedRowKeys as string[]);
    },
    onSelect: (record: any, selected: boolean, selectedRows: any[], nativeEvent: Event) => {
    },
    onSelectAll: (selected: boolean, selectedRows: any[], changeRows: any[]) => {
    },
    getCheckboxProps: (record: any) => ({
      name: record.id,
      disabled: record.status?.toUpperCase() === 'PENDING' ||
        record.status?.toUpperCase() === 'PENDING_APPROVAL' ||
        record.status?.toUpperCase() !== 'DRAFT' &&
        record.status?.toUpperCase() !== 'REJECTED'
    }),
  };

  // Table Configuration: Column definitions for the reports table
  const reportColumns: ColumnType<ExpenseReport>[] = [
    {
      title: "Expense Header Name",
      dataIndex: "reportName",
      key: "reportName",
      width: 270,
      render: (text: string, record: ExpenseReport) => (
        <span
          className="inlineTableCellLink"
          style={{
            display: "inline-block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            verticalAlign: "middle",
            cursor: "pointer",
            color: "#2557d6",
            fontWeight: "600",
            fontSize: "14px",
            fontFamily: "Inter, Roboto, sans-serif"
          }}
          onClick={() => handleOpenReport(record)}
          title={text}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Duration",
      dataIndex: "dateRange",
      key: "dateRange",
      width: 200,
      render: (text: string) => (
        <div style={{ fontSize: '14px', fontFamily: 'Inter, Roboto, sans-serif' }}>{text}</div>
      ),
    },
    {
      title: "Total In",
      dataIndex: "userTotalIn",
      key: "userTotalIn",
      width: 100,
      align: "right",
      render: (value: number) => (
        <div style={{ fontSize: '14px', fontFamily: 'Inter, Roboto, sans-serif', textAlign: 'right', paddingRight: '8px' }}>
          ₹{(value || 0).toLocaleString()}
        </div>
      ),
    },
    {
      title: "Total Out",
      dataIndex: "userTotalOut",
      key: "userTotalOut",
      width: 100,
      align: "right",
      render: (value: number) => (
        <div style={{ fontSize: '14px', fontFamily: 'Inter, Roboto, sans-serif', textAlign: 'right', paddingRight: '8px' }}>
          ₹{(value || 0).toLocaleString()}
        </div>
      ),
    },
    {
      title: "Reimbursement",
      dataIndex: "userReimbursableAmount",
      key: "userReimbursableAmount",
      width: 100,
      align: "right",
      render: (value: number) => (
        <div style={{ fontSize: '14px', fontFamily: 'Inter, Roboto, sans-serif', textAlign: 'right', paddingRight: '8px' }}>
          ₹{(value || 0).toLocaleString()}
        </div>
      ),
    },
    {
      title: "Balance",
      dataIndex: "userBalance",
      key: "userBalance",
      width: 140,
      align: "right",
      render: (value: number) => (
        <div style={{
          fontSize: '14px',
          fontFamily: 'Inter, Roboto, sans-serif',
          // color: (value || 0) < 0 ? '#ff4d4f' : (value || 0) > 0 ? '#52c41a' : '#666',
          textAlign: 'right',
          paddingRight: '8px'
        }}>
          ₹{(value || 0).toLocaleString()}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      // fixed: 'right',
      width: 100,
      render: (status: string) => (
        <Tag
          color={
            status.toLowerCase().includes("draft") ? "geekblue" :
              status.toLowerCase().includes("pending") ? "gold" :
                status.toLowerCase().includes("approved") ? "green" :
                  status.toLowerCase().includes("rejected") ? "red" : "default"
          }
          style={{ fontSize: 14, padding: '2px 12px' }}
        >
          {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 60,
      render: (_: any, record: ExpenseReport) => {
        const menuItems = [];

        // Add Send for Approval for DRAFT and REJECTED status
        if (record.status?.toUpperCase() === 'DRAFT' || record.status?.toUpperCase() === 'REJECTED') {
          menuItems.push({
            key: 'send-approval',
            label: (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
                <Icon name="send" style={{ fontSize: '12px' }} />
                <span style={{ fontSize: '12px', fontFamily: 'Inter, Roboto, sans-serif' }}>Send for Approval</span>
              </div>
            ),
            onClick: () => handleSendReportForApproval(record.id)
          });
        }

        // Add Edit for non-APPROVED status
        if (record.status?.toUpperCase() !== 'APPROVED') {
          menuItems.push({
            key: 'edit',
            label: (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
                <Icon name="edit" style={{ fontSize: '12px' }} />
                <span style={{ fontSize: '12px', fontFamily: 'Inter, Roboto, sans-serif' }}>Edit</span>
              </div>
            ),
            onClick: () => handleEditReport(record)
          });
        }

        // Add Delete for non-APPROVED status
        if (record.status?.toUpperCase() !== 'APPROVED') {
          menuItems.push({
            key: 'delete',
            label: (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', color: '#ff4d4f' }}>
                <Icon name="trash" style={{ fontSize: '12px' }} />
                <span style={{ fontSize: '12px', fontFamily: 'Inter, Roboto, sans-serif' }}>Delete</span>
              </div>
            ),
            onClick: () => handleDeleteReport(record.id)
          });
        }

        // Always add View
        menuItems.push({
          key: 'view',
          label: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
              <Icon name="eye" style={{ fontSize: '12px' }} />
              <span style={{ fontSize: '12px', fontFamily: 'Inter, Roboto, sans-serif' }}>View</span>
            </div>
          ),
          onClick: () => handleIdClick(record.id)
        });

        const menu = <Menu items={menuItems} style={{ minWidth: '180px' }} />;

        return (
          <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
            <a
              style={{
                border: 'none',
                background: 'transparent',
                padding: '4px 8px',
                height: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                textDecoration: 'none'
              }}
            >
              <Icon name="more-h" style={{ fontSize: '16px', color: '#666' }} />
            </a>
          </Dropdown>
        );
      },
    }
  ];

  return (
    <>
      <Head title="Expense Reports" />
      <Content>
        <Card className="card-bordered" style={{ margin: "0", overflow: "hidden" }}>
          <div
            className="card-inner card-inner-lg"
            style={{
              padding: "0",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column"
            }}
          >

              <FormProvider {...methods}>
                <form style={{ height: "100%", display: "flex", flexDirection: "column" }}>

                  {/* Tabs Section */}
                  <Tabs
                    activeKey={activeTab}
                    onChange={(key) => setActiveTab(key)}
                    tabBarStyle={{
                      margin: 0,
                      padding: "0 16px",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                    tabBarExtraContent={{
                      right: (
                        <div style={{ display: "flex", gap: "8px", alignItems: "center", paddingRight: "8px" }}>
                          {topRightJSX}
                          <Button
                            color="primary"
                            size="sm"
                            onClick={() => {
                              const resolvedProjectId = projectId || projectIdFromUrl || location.state?.projectId;
                              navigate(resolvedProjectId ? `/pms/expenses/new-header/${resolvedProjectId}` : '/pms/expenses/new-header', {
                                state: {
                                  ...location.state,
                                  id: resolvedProjectId,
                                  projectId: resolvedProjectId,
                                  projectName: projectName || location.state?.projectName,
                                  siteId: siteId || location.state?.siteId,
                                }
                              });
                            }}
                            style={{ display: "flex", alignItems: "center", gap: "4px" }}
                          >
                            <PlusOutlined />
                            <span>New Header</span>
                          </Button>
                          <Button
                            color="light"
                            size="sm"
                            onClick={() => refreshListView()}
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <ReloadOutlined />
                          </Button>
                        </div>
                      ),
                    }}
                    items={[
                      { key: "all", label: "All" },
                      { key: "draft", label: "Draft" },
                      { key: "pending", label: "Pending" },
                      { key: "approved", label: "Approved" },
                      { key: "rejected", label: "Rejected" },
                    ]}
                  />

                  {/* Table Content Section */}
                  <div
                    style={{
                      flex: "1",
                      padding: "10px",
                      overflow: "visible",
                      backgroundColor: "#fff",
                      position: "relative",
                      zIndex: 1
                    }}
                  >
                    <SkeletonTable
                      loading={loading}
                      columns={reportColumns as SkeletonTableColumnsType[]}
                    >
                      <Table
                        rowKey="id"
                        columns={reportColumns}
                        dataSource={filteredReports}
                        pagination={pagination}
                        onChange={tableOnChange}
                        rowSelection={rowSelection}
                        bordered
                        size="middle"
                        scroll={{
                          x: 1200,
                        }}
                        style={{
                          position: 'relative',
                          zIndex: 1,
                          overflow: 'visible'
                        }}
                        components={{
                          body: {
                            wrapper: (props: any) => (
                              <tbody {...props} style={{ position: 'relative', zIndex: 1 }} />
                            )
                          }
                        }}
                        locale={{
                          emptyText: (
                            <div style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              height: "200px",
                              backgroundColor: "white",
                            }}>
                              <Empty description="No Data Found " />
                            </div>
                          ),
                        }}
                      />
                    </SkeletonTable>
                  </div>
                </form>
              </FormProvider>
          </div>
        </Card>
      </Content>
      {/* Report Detail Modal */}
      <ReportInDetailModal />

    </>
  );
};

export default Reports;